import React, { useState, useEffect } from 'react';
import { Modal } from '../Common/Modal';
import auditService from '../../services/auditService';
import { getAuditFieldDisplayName } from '../../utils/auditFieldDisplayNames';
import { formatDateTimeDDMMMYYYY, formatOffsetForDisplay, formatPerformedOnUtcToLocal } from '../../utils/dateFormat';
import Spinner from '../Common/Spinner';
import './AuditDetailModal.css';

/** Performed on UTC: shown as local time (UTC + offset), e.g. 25-Feb-2026 18:18 */
const formatPerformedOnUtc = (d, offset) => (d ? formatPerformedOnUtcToLocal(d, offset) : '—');
const cell = (v, maxLen = 80) => {
  if (v == null || v === '') return '—';
  const s = String(v);
  return s.length > maxLen ? s.slice(0, maxLen) + '…' : s;
};

/**
 * Modal that shows field-level audit for a site field using the dedicated API:
 * GET /api/audit/site/{siteNo}/field/{fieldName}
 * (AuditHdr + AuditDtl + SiteMst by nSiteNo and vFieldName)
 * Pass projectId / siteId (or projectCode) so AuditHdr rows filter by nProjectId / nSiteId.
 */
const SiteFieldAuditModal = ({
  isOpen,
  onClose,
  siteNo,
  fieldName,
  /** When set (from the form label of the clicked audit button), shown in "Field Name" instead of raw vFieldName. */
  fieldDisplayLabel,
  title = 'Audit Detail',
  projectId,
  siteId,
  projectCode,
  overlayClassName = '',
  className = ''
}) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen || siteNo == null || siteNo === '' || !fieldName) {
      setRows([]);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    auditService
      .getSiteFieldAudit(siteNo, fieldName, 100, { projectId, siteId, projectCode })
      .then((data) => {
        if (!cancelled) setRows(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.response?.data?.error || err?.message || 'Failed to load field audit');
          setRows([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [isOpen, siteNo, fieldName, projectId, siteId, projectCode]);

  const footer = (
    <button type="button" className="btn btn-secondary" onClick={onClose}>
      Close
    </button>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="large"
      footer={footer}
      showCloseButton={true}
      overlayClassName={overlayClassName}
      className={className}
    >
      <div className="audit-detail-modal-body">
        {loading && (
          <div className="audit-detail-loading">
            <Spinner />
            <span>Loading audit trail…</span>
          </div>
        )}
        {error && (
          <div className="audit-detail-error alert alert-danger">
            {error}
          </div>
        )}
        {!loading && !error && rows.length === 0 && (
          <div className="audit-detail-empty text-muted">
            No audit entries found for this field.
          </div>
        )}
        {!loading && !error && rows.length > 0 && (
          <div className="table-wrap">
            <table className="audit-table audit-detail-table">
              <thead>
                <tr>
                  <th>Field Name</th>
                  <th>Old Value</th>
                  <th>New Value</th>
                  <th>Performed By</th>
                  <th>Performed on UTC</th>
                  <th>Performed At Offset</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, idx) => {
                  const rawField =
                    r.vFieldName ?? r.VFieldName ?? fieldName;
                  const displayField =
                    fieldDisplayLabel != null && String(fieldDisplayLabel).trim() !== ''
                      ? String(fieldDisplayLabel).trim()
                      : getAuditFieldDisplayName(rawField, r.vTableName ?? r.VTableName);
                  return (
                  <tr key={idx}>
                    <td title={rawField}>{cell(displayField, 40)}</td>
                    <td title={r.vOldValue ?? r.VOldValue}>{cell(r.vOldValue ?? r.VOldValue)}</td>
                    <td title={r.vNewValue ?? r.VNewValue}>{cell(r.vNewValue ?? r.VNewValue)}</td>
                    <td title={r.performedBy || r.vRecordedSign}>{cell(r.performedBy || r.vRecordedSign, 40)}</td>
                    <td>{formatPerformedOnUtc(r.dRecordedOnUTC ?? r.dRecordedOnUtc, r.vRecordedAtOffSet ?? r.vRecordedAtOffset)}</td>
                    <td>{formatOffsetForDisplay(r.vRecordedAtOffSet ?? r.vRecordedAtOffset) === '—' ? '—' : formatOffsetForDisplay(r.vRecordedAtOffSet ?? r.vRecordedAtOffset)}</td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default SiteFieldAuditModal;
